import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    Package,
    Plus,
    Clock,
    Users,
    CheckCircle2,
    Trash2,
    ShoppingBag,
    TrendingUp
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';
import { StatusBadge } from './ui/StatusBadge';

const GroupBuyManager: React.FC = () => {
    const [groupBuys, setGroupBuys] = useState<any[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [goal, setGoal] = useState('');
    const [deadline, setDeadline] = useState('');
    const [unitPrice, setUnitPrice] = useState('');
    const [discountPrice, setDiscountPrice] = useState('');

    useEffect(() => { fetchGB(); }, []);

    const fetchGB = async () => {
        const res = await api.get('/group-buys');
        setGroupBuys(res.data);
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/group-buys', {
                title,
                description,
                goal_quantity: parseInt(goal),
                unit_price: parseFloat(unitPrice),
                discount_price: parseFloat(discountPrice),
                deadline: deadline || null
            });
            fetchGB();
            setIsCreating(false);
            setTitle(''); setDescription(''); setGoal(''); setDeadline(''); setUnitPrice(''); setDiscountPrice('');
        } catch (e) { alert('Creation failed'); }
    };

    const handleApprove = async (id: string) => {
        await api.patch(`/group-buys/${id}/approve`);
        fetchGB();
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Group Buys" subtitle="Collective buying power for residents">
                <button
                    onClick={() => setIsCreating(!isCreating)}
                    className="bg-violet-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-violet-700 transition-all text-sm"
                >
                    {isCreating ? 'Cancel' : 'New Deal'}
                    <Plus size={18} className={cn("transition-transform", isCreating && "rotate-45")} />
                </button>
            </PageHeader>

            {isCreating && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-in slide-in-from-top-2 duration-300">
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Deal Title</label>
                                <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="e.g. Bulk Mangoes from Ratnagiri" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Goal Quantity</label>
                                <input value={goal} onChange={e => setGoal(e.target.value)} type="number" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Regular Price</label>
                                <input value={unitPrice} onChange={e => setUnitPrice(e.target.value)} type="number" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Discounted Price</label>
                                <input value={discountPrice} onChange={e => setDiscountPrice(e.target.value)} type="number" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1 md:col-span-2">
                                <label className="text-xs font-semibold text-slate-500">Deadline</label>
                                <input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1 md:col-span-2">
                                <label className="text-xs font-semibold text-slate-500">Description</label>
                                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                            </div>
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all">
                                Launch Group Buy
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupBuys.map((gb) => {
                    const progress = Math.min((gb.current_quantity / gb.goal_quantity) * 100, 100);
                    return (
                        <div key={gb.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:border-violet-200 transition-all group">
                            <div className="p-5 flex-1">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                                        <ShoppingBag size={20} />
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <StatusBadge status={gb.status} />
                                        <p className="text-[10px] text-slate-400 font-medium">Ends: {new Date(gb.deadline).toLocaleDateString()}</p>
                                    </div>
                                </div>

                                <h3 className="font-bold text-slate-900 line-clamp-1 group-hover:text-violet-600 transition-colors">{gb.title}</h3>
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{gb.description}</p>

                                <div className="mt-4 flex items-center gap-3">
                                    <div className="text-lg font-bold text-slate-900">₹{gb.discount_price}</div>
                                    <div className="text-xs text-slate-400 line-through font-medium">₹{gb.unit_price}</div>
                                    <div className="ml-auto text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                                        SAVE {Math.round((1 - gb.discount_price/gb.unit_price)*100)}%
                                    </div>
                                </div>

                                <div className="mt-6 space-y-2">
                                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                        <span>{gb.current_quantity} / {gb.goal_quantity} Units</span>
                                        <span>{Math.round(progress)}%</span>
                                    </div>
                                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-violet-500 rounded-full transition-all duration-1000"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-50 px-5 py-3 flex items-center justify-between border-t border-slate-100">
                                {gb.status === 'proposed' ? (
                                    <button onClick={() => handleApprove(gb.id)} className="text-[10px] font-bold uppercase text-emerald-600 hover:text-emerald-700">Approve Proposal</button>
                                ) : (
                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                        <Users size={12} /> ID: {gb.id.slice(0,6)}
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <button className="p-1.5 text-slate-300 hover:text-red-500 transition-colors">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GroupBuyManager;
